import { toast } from "react-toastify";
import { call, put, takeLatest } from "redux-saga/effects";
import * as actions from "./Actions";
import * as api from "./Api";
import Types from "./Types";

function* getAllStudents(formData) {
  try {
    yield put(actions.addStudentLoading(true));
    const result = yield call(api.getAllStudents, formData);
    yield put(actions.getAllStudentsSuccess(result.data));
  } catch (error) {
    const { response, message } = error;
    toast.error(error.response?.data?.message || error.message || "Failed to fetch students");
    yield put(actions.getAllStudentsFail());
  } finally {
    yield put(actions.addStudentLoading(false));
  }
}
function* getAllStudentsForCsv(formData) {
  try {
    yield put(actions.addStudentLoading(true));
    const result = yield call(api.getStudentsForCsv, formData);
    yield put(
      actions.getAllStudentsForCsvSuccess({ data: result.data, formData })
    );
  } catch (error) {
    const { response, message } = error;
    toast.error(error.response?.data?.message || error.message || "Failed to fetch students");
    yield put(actions.getAllStudentsForCsvFail());
  } finally {
    yield put(actions.addStudentLoading(false));
  }
}

function* getStudentsForBulkMessage({ payload }) {
  try {
    yield put(actions.addStudentLoading(true));
    const result = yield call(api.getStudentsForBulkMessage, payload);
    yield put(actions.getStudentsForBulkMessageSuccess(result));
  } catch (error) {
    const { response, message } = error;
    toast.error(error.response?.data?.message || error.message || "Failed to fetch students");
    yield put(actions.getStudentsForBulkMessageFail());
  } finally {
    yield put(actions.addStudentLoading(false));
  }
}
function* clearSession({ payload }) {
  try {
    yield put(actions.addStudentLoading(true));
    const result = yield call(api.clearSession, payload);
    toast.success("session cleared");

    yield put(actions.clearSessionSuccess(result.data));
  } catch (error) {
    const { response, message } = error;
    toast.error(error.response?.data?.message || error.message || "Failed to fetch students");
    yield put(actions.clearSessionFail());
  } finally {
    yield put(actions.addStudentLoading(false));
  }
}

//Edit Tables
function* editStudent({ payload }) {
  try {
    yield put(actions.addStudentLoading(true));
    const result = yield call(api.editStudents, payload);
    yield put(actions.editStudentSuccess(result.data));
    toast.success("Edit Successfully");
    payload?.action && payload?.action();
  } catch (error) {
    yield put(actions.editStudentFail());
  } finally {
    yield put(actions.addStudentLoading(false));
  }
}

// create
function* createStudent(formData) {
  try {
    yield put(actions.addStudentLoading(true));
    const result = yield call(api.createStudents, formData.payload);
    // Check for error response in result.data
    if (
      result?.data?.state === "error" &&
      result?.data?.message
    ) {
      toast.error(result.data.message);
      yield put(actions.createStudentFail());
    } else {
      yield put(
        actions.createStudentSuccess({
          data: result.data,
        })
      );
      toast.success("Created Successfully");
      formData?.action && formData?.action(result?.data?.data);
    }
  } catch (error) {
    // Fallback for other errors
    if (
      error?.response?.data?.code === 400 &&
      error?.response?.data?.state === "error" &&
      error?.response?.data?.message
    ) {
      toast.error(error.response.data.message);
    } else {
      toast.error(error?.message || error);
    }
    yield put(actions.createStudentFail());
  } finally {
    yield put(actions.addStudentLoading(false));
  }
}

function* studentReport({ payload }) {
  try {
    yield put(actions.suspendLoading(true));
    const result = yield call(api.getStudentReport, payload);

    yield put(actions.getStudentReportSuccess({ data: result?.data }));
  } catch (error) {
    const { response, message } = error;
    toast.error(error.response);
    yield put(actions.getStudentReportFail());
  } finally {
    yield put(actions.suspendLoading(false));
  }
}

function* deleteStudent({ payload, reason }) {
  try {
    yield put(actions.suspendLoading(true));
    const result = yield call(api.deleteStudent, payload, reason);

    // Validate result before dispatching success
    if (result && result.data) {
      yield put(actions.deleteStudentSuccess({ payload, result }));
      toast.success("Deleted Successfully");
    } else {
      throw new Error("Invalid response from server");
    }
  } catch (error) {
    const { response, message } = error;
    toast.error(error.response?.data?.message || error.message || "Failed to delete student");
    yield put(actions.deleteStudentFail());
  } finally {
    yield put(actions.suspendLoading(false));
  }
}

function* getStudent({ payload }) {
  try {
    yield put(actions.suspendLoading(true));
    const result = yield call(api.getStudent, payload);

    yield put(actions.getStudentSuccess(result.data));
  } catch (error) {
    const { response, message } = error;
    toast.error(error.response);
    yield put(actions.getStudentFail());
  } finally {
    yield put(actions.suspendLoading(false));
  }
}

function* restrictStudent({ payload }) {
  try {
    yield put(actions.restrictLoading(true));
    const result = yield call(api.restricStudent, payload);

    // Validate result before dispatching success
    if (result && result.data) {
      yield put(actions.restrictStudentSuccess({ payload, result }));
      toast.success("Restricted Successfully");
    } else {
      throw new Error("Invalid response from server");
    }
  } catch (error) {
    const { response, message } = error;
    toast.error(error.response?.data?.message || error.message || "Failed to restrict student");
    yield put(actions.restrictStudentFail());
  } finally {
    yield put(actions.restrictLoading(false));
  }
}

function* addBulkStudent({ payload }) {
  try {
    yield put(actions.addStudentLoading(true));
    const result = yield call(api.addBulkStudent, payload);
    yield put(actions.addBulkStudentSuccess(payload));
    toast.success("Students Added Successfully");
  } catch (error) {
    const { response, message } = error;
    toast.error("Something Wrong");
    yield put(actions.addBulkStudentFail());
  } finally {
    yield put(actions.addStudentLoading(false));
  }
}

function* getStudentsRequestsList(formData) {
  try {
    yield put(actions.addStudentLoading(true));
    const result = yield call(api.getStudentsRequests, formData);
    yield put(actions.studentsRequestsListSuccess(result.data));
  } catch (error) {
    const { response, message } = error;
    toast.error(error.response?.data?.message || error.message || "Failed to fetch students");
  } finally {
    yield put(actions.addStudentLoading(false));
  }
}

function* getApprovedStudentsList({ payload, searchTerm, studentGroupId }) {
  try {
    yield put(actions.addStudentLoading(true));
    const result = yield call(api.getAprovedStudents, { payload, searchTerm, studentGroupId });
    yield put(actions.approvedStudentsListSuccess(result.data));
  } catch (error) {
    const { response, message } = error;
    toast.error(error.response?.data?.message || error.message || "Failed to fetch students");
  } finally {
    yield put(actions.addStudentLoading(false));
  }
}

function* approveStudent({ payload }) {
  try {
    yield put(actions.suspendLoading(true));
    const result = yield call(api.approveStudent, payload);
    yield put(actions.approveStudentSuccess({ payload, result }));
    toast.success("Approved Successfully");
  } catch (error) {
    const { response, message } = error;
    toast.error(error.response);
  } finally {
    yield put(actions.suspendLoading(false));
  }
}

function* rejectStudent({ payload }) {
  try {
    yield put(actions.restrictLoading(true));
    const result = yield call(api.rejectStudent, payload);
    yield put(actions.rejectStudentSuccess({ payload, result }));
    toast.success("Deleted Successfully");
  } catch (error) {
    const { response, message } = error;
    toast.error(error.response);
  } finally {
    yield put(actions.restrictLoading(false));
  }
}

function* resendPassword({ payload }) {
  try {
    yield put(actions.addStudentLoading(true));
    const result = yield call(api.resendPassword, payload);
    yield put(actions.resendPasswordSuccess(result.data));
    toast.success("Password resent successfully");
  } catch (error) {
    const { response, message } = error;
    toast.error(error.response?.data?.message || "Failed to resend password");
    yield put(actions.resendPasswordFail());
  } finally {
    yield put(actions.addStudentLoading(false));
  }
}

function* sendCredentials({ payload }) {
  try {
    const result = yield call(api.sendCredentials, payload);
    yield put(actions.sendCredentialsSuccess(result.data));
    toast.success("Credentials sent successfully");
  } catch (error) {
    const { response, message } = error;
    toast.error(error.response?.data?.message || "Failed to send credentials");
    yield put(actions.sendCredentialsFail());
  } finally {
    yield put(actions.addStudentLoading(false));
  }
}

function* getInactiveStudents({ payload, searchTerm, flag }) {
  try {
    const result = yield call(api.getInactiveStudents, { payload, searchTerm, flag });
    yield put(actions.getInactiveStudentsSuccess(result.data));
  } catch (error) {
    const { response, message } = error;
    toast.error(error.response?.data?.message || "Failed to get inactive students");
    yield put(actions.getInactiveStudentsFail());
  } finally {
    yield put(actions.addStudentLoading(false));
  }
}

export default function* StudentSaga() {
  yield takeLatest(Types.GET_STUDENTS_REQUEST, getAllStudents);
  yield takeLatest(Types.EDIT_STUDENT_REQUEST, editStudent);
  yield takeLatest(Types.CREATE_STUDENT_REQUEST, createStudent);
  yield takeLatest(Types.DELETE_STUDENT_REQUEST, deleteStudent);
  yield takeLatest(Types.ADD_BULK_STUDENT_REQUEST, addBulkStudent);
  yield takeLatest(Types.RESTRICT_STUDENT_REQUEST, restrictStudent);
  yield takeLatest(Types.CLEAR_SESSION_REQUEST, clearSession);
  yield takeLatest(Types.GET_STUDENT_REQUEST, getStudent);
  yield takeLatest(Types.GET_STUDENT_REPORT_REQUEST, studentReport);
  yield takeLatest(
    Types.STUDENT_REQUESTS_LIST_REQUEST,
    getStudentsRequestsList
  );
  yield takeLatest(
    Types.STUDENT_APPROVED_LIST_REQUEST,
    getApprovedStudentsList
  );
  yield takeLatest(Types.APPROVE_STUDENT_REQUEST, approveStudent);
  yield takeLatest(Types.REJECT_STUDENT_REQUEST, rejectStudent);
  yield takeLatest(Types.RESEND_PASSWORD_REQUEST, resendPassword);
  yield takeLatest(Types.GET_STUDENTS_FOR_CSV_REQUEST, getAllStudentsForCsv);
  yield takeLatest(Types.GET_STUDENTS_FOR_BULK_MESSAGE_REQUEST, getStudentsForBulkMessage);
  yield takeLatest(Types.SEND_CREDENTIALS_REQUEST, sendCredentials);
  yield takeLatest(Types.GET_INACTIVE_STUDENTS_REQUEST, getInactiveStudents);
}
